"use client";

import type { CSSProperties } from "react";
import { BoardTouchControls } from "./BoardTouchControls";
import { useTouchControlsSettings } from "../hooks/useTouchControlsSettings";
import type { LegacyAction } from "./TouchControls";

export type ActiveCell = {
  x: number;
  y: number;
  piece: number;
};

function landingCells(board: readonly (readonly number[])[], active: readonly ActiveCell[]): ActiveCell[] {
  if (active.length === 0) return [];

  let distance = 0;
  while (active.every(({ x, y }) => {
    const nextY = y + distance + 1;
    return nextY >= 1 && nextY <= 24 && board[nextY - 1]?.[x - 1] === 0;
  })) {
    distance += 1;
  }

  if (distance <= 0) return [];
  return active.map((cell) => ({ ...cell, y: cell.y + distance }));
}

type BattleBoardProps = {
  active?: ActiveCell[];
  activeBlockIndex?: number;
  board: number[][];
  label: string;
  local?: boolean;
  controlsDisabled?: boolean;
  onInput?: (action: LegacyAction, held: boolean) => void;
  topOut?: boolean;
};

const cellStyle = (value: number): CSSProperties =>
  ({ "--piece": value } as CSSProperties);

export function BattleBoard({
  active = [],
  activeBlockIndex,
  board,
  label,
  local = false,
  controlsDisabled = false,
  onInput,
  topOut = false,
}: BattleBoardProps) {
  const { settings } = useTouchControlsSettings();
  const activeByIndex = new Map(
    active
      .filter(({ x, y }) => x >= 1 && x <= 10 && y >= 1 && y <= 24)
      .map((cell) => [((cell.y - 1) * 10 + (cell.x - 1)), cell.piece]),
  );
  const ghostByIndex = local && settings.ghostVisible
    ? new Map(
      landingCells(board, active)
        .filter(({ x, y }) => x >= 1 && x <= 10 && y >= 1 && y <= 24)
        .map((cell) => [((cell.y - 1) * 10 + (cell.x - 1)), cell.piece]),
    )
    : new Map<number, number>();

  const cells = Array.from({ length: 240 }, (_, index) => {
    const row = Math.floor(index / 10);
    const column = index % 10;
    const fixed = board[row]?.[column] ?? 0;
    const moving = activeByIndex.get(index) ?? 0;
    const ghost = moving || fixed ? 0 : ghostByIndex.get(index) ?? 0;
    const value = moving || fixed || ghost;

    return (
      <span
        aria-hidden="true"
        className={`board-cell${value ? " tile" : ""}${moving ? " active-tile" : ""}${ghost ? " ghost-tile" : ""}`}
        key={index}
        style={cellStyle(value)}
      />
    );
  });

  return (
    <section
      aria-label={label}
      className={`battle-board${local ? " local-board" : ""}${topOut ? " top-out" : ""}`}
    >
      <div className="board-bevel">
        <div className="board-well">
        <div className="board-grid">{cells}</div>
        <div aria-hidden="true" className="defeat-zone" />
        {local && onInput ? (
          <BoardTouchControls
            active={active}
            activeBlockIndex={activeBlockIndex}
            disabled={controlsDisabled}
            onInput={onInput}
          />
        ) : null}
      </div>
      </div>
      <div className="seat-label">
        <span className="seat-dot" />
        {label}
      </div>
    </section>
  );
}
